import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  OnChanges,
  OnDestroy,
  ChangeDetectionStrategy,
  SimpleChanges,
  forwardRef,
  AfterViewInit,
} from '@angular/core';
import { AbstractControl, FormArray, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { MatrixService } from '@app/matrix/services/matrix.service';
import { MatTableDataSource } from '@angular/material/table';
import { UtilityAccountService, ClassValidation } from '@app/shared/services/utility-account.service';
import { UtilityAccount } from '@app/shared/models/UtilityAccount';
import { MessagingService } from '@app/shared/services/messaging.service';
import { MatDatepicker } from '@angular/material/datepicker';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import * as _moment from 'moment';
import { default as _rollupMoment, Moment } from 'moment';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { ApplyToAllComponent } from '@app/shared/components/apply-to-all/apply-to-all.component';
import { UtilityRateCode } from '@app/shared/models/utility-rate-code';
import { AppState } from '@app/state/app.state';
import { Store } from '@ngrx/store';
import { UtilityHubConfiguration } from "@configuration/models/utilityhub-configuration";
import { UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { getAccountName, UtilityInformation, UtilityZoneInformation } from '@app/shared/models/utility-information';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { QuickQuoteUtilityService } from '@app/quick-quote/services/quick-quote-utilily.service';
import { SalesType } from '@app/shared/enums/sales-type';
import { QuoteRequestState } from '@app/shared/models/quote-request-state';
import { loggedInUserSelector, loggedInUtilityHubIdentitySelector, configurationSelector, isReadOnlySelector } from '@app/state/app.selectors';
import { salesTypeSelector, supplierUtilityInformationSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { QuoteConfiguration, ShowControls, UtilityDetails } from '@app/shared/models/customer-details';
import { UsAddressLookupService } from '@app/shared/services/us-address-lookup.service';
import { catchError, finalize, take } from 'rxjs/operators';
import { UtilityInformationService } from '@app/shared/services/utility-information.service';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { SupplierUtilityModel } from '@app/shared/models/supplier-utility-model';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MMM-YYYY,yyyy/mm/dd',
  },
  display: {
    dateInput: 'MMM-YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-utility-accounts-grid',
  templateUrl: './utility-accounts-grid.component.html',
  styleUrls: ['./utility-accounts-grid.component.scss'],
  providers: [
    // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
    // application's root module. We provide it at the component level here, due to limitations of
    // our example generation script.
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UtilityAccountsGridComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => UtilityAccountsGridComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UtilityAccountsGridComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() mandatoryLoadProfile: string;
  @Input() mandatoryStartDate: string;
  @Input() annualUsageRequired: boolean;
  @Input() nextButtonClicked: Observable<void>;
  @Input() quoteRequestState: QuoteRequestState;
  @Input() userIdentity: UtilityHubIdentity;
  @Input() showRateCode: boolean = true;
  @Input() showStartDate: boolean = true;
  @Input() title: string;
  @Input() description: string;
  isValidProfile: boolean;
  lookup$: any;
  zipCode: string;
  footerZipCodeInvalid: boolean;
  footerCityInvalid: boolean;
  footerStateInvalid: boolean;
  footerStreetAddress1Invalid: boolean;
  footerUtilityInvalid: boolean;
  footerIsoInvalid: boolean;
  footerZoneInvalid: boolean;
  footerWeatherZoneInvalid: boolean;
  utilityAccountNumber: string;
  utilityInformation$: Subscription;
  utilityRateCodes$: Subscription;
  zipCodeLoadingMap: { [key: string]: boolean } = {};
  isGas: boolean;
  supplierUtilityInformation: SupplierUtilityModel[];
  @Input() set quoteRequestId(value) {
    this.quoteRequestIdpv = value;
  }
  nextButtonClicked$: Subscription;
  selectLoggedInUser$: Subscription;
  uhIdentity$: Subscription;
  uhConfig$: Subscription;
  get quoteRequestId() {
    return this.quoteRequestIdpv;
  }

  @Output() init = new EventEmitter();

  public get minDate() {
    return new Date();
  }
  private quoteRequestIdpv: number;
  public utilityInformation: UtilityInformation;
  public control: FormArray;
  public dataSource: MatTableDataSource<UtilityAccount> = new MatTableDataSource([]);
  public newAccountsData: UtilityAccount;
  public utilityAccounts: UtilityAccount[] = null;
  public errorMessage: string;
  public validityChecked = false;
  anonymousObject: { valid: true, entities: ClassValidation[] };
  public footerRateCodeInvalid = false;
  public footerUanInvalid = false;
  public footerStartDateInvalid = false;

  public footerNameKeyInvalid = false;
  public footerServiceKeyInvalid = false;
  footerMeterNumberInvalid: boolean = false;
  dontShowMeterNumberColumn: boolean = true;  /**will always be true until a history is created to validate the combination of utilityAccountNumber and MeterNumber throughout the system, if in doubt talk to JC. */

  public get footerPartiallyCompleted(): boolean {
    if (this.validateKeyAndMeterNumber === true) {
      let x = this.uanFooter?.nativeElement.value ||
        this.rateCodeFooter?.nativeElement.value ||
        this.startDateFooter?.nativeElement.value ||
        (this.utilityInformation.useNameKey === true ? this.nameKeyFooter?.nativeElement.value : false) ||
        (this.utilityInformation.useServiceNumber === true ? this.serviceKeyFooter?.nativeElement.value : false) ||
        (this.utilityInformation.meterNumberIsMandatory === true ? this.meterNumberFooter?.nativeElement.value : false);
      return x;
    } else {
      return this.uanFooter?.nativeElement.value || this.rateCodeFooter?.nativeElement.value || this.startDateFooter?.nativeElement.value;
    }
  }

  public displayedColumns: string[] = [];

  private uhConfig: UtilityHubConfiguration;
  frmUtilityAccountsGrid: FormGroup;
  public onTouched: () => void = () => { };
  private updated = new Subject<string>();
  register$: Subscription;
  salesType$: Observable<string>;
  private isReadOnly$: Subscription;
  public isReadOnly: boolean;
  showValidation: boolean = false;
  showControls: ShowControls = {
    showIso: true,
    showZone: true,
    showBundledRateCheckbox: false,
    loadingUtilities: false,
    emitEventOnValueChange: true,
  };
  loadingUtilities: boolean = false;
  emitEventOnValueChange = true;
  quoteConfiguration: QuoteConfiguration;
  supplierUtilityInformation$: Subscription;

  @ViewChild(ApplyToAllComponent) applyToAll: ApplyToAllComponent;
  @ViewChild('uanFooter') uanFooter: ElementRef;
  @ViewChild('rateCodeFooter') rateCodeFooter: ElementRef;
  @ViewChild('startDateFooter') startDateFooter: ElementRef;
  @ViewChild('nameKeyFooter') nameKeyFooter: ElementRef;
  @ViewChild('serviceKeyFooter') serviceKeyFooter: ElementRef;
  @ViewChild('meterNumberFooter') meterNumberFooter: ElementRef;
  @ViewChild('zipCodeFooter') zipCodeFooter: ElementRef;
  @ViewChild('cityFooter') cityFooter: ElementRef;
  @ViewChild('stateFooter') stateFooter: ElementRef;
  @ViewChild('streetAddress1Footer') streetAddress1Footer: ElementRef;
  @ViewChild('streetAddress2Footer') streetAddress2Footer: ElementRef;
  @ViewChild('utilityFooter') utilityFooter: ElementRef;
  @ViewChild('isoFooter') isoFooter: ElementRef;
  @ViewChild('zoneFooter') zoneFooter: ElementRef;
  @ViewChild('weatherZoneFooter') weatherZoneFooter: ElementRef;

  public getAccountName = getAccountName;
  validateKeyAndMeterNumber: boolean;

  requestedRateCodeUtilities: string[] = [];
  madeRequestForUtilityInformation: { [key: string]: boolean } = {};
  madeRequestForRatecodes: { [key: string]: boolean } = {};
  loadingRateCodes: { [key: string]: boolean } = {};

  constructor(
    public utilityAccountService: UtilityAccountService,
    private messaging: MessagingService,
    public quoteService: MatrixService,
    public quoteRequestService: QuoteRequestService,
    private store: Store<AppState>,
    private quickQuoteUtilityService: QuickQuoteUtilityService,
    private usLookup: UsAddressLookupService,
    private utilityInformationService: UtilityInformationService,
  ) {
    this.salesType$ = store.select(salesTypeSelector);
  }

  ngAfterViewInit(): void {
    this.initNewAccountsData();

    if (this.newAccountsData?.address?.zipCode) {
      this.zipLookup(this.newAccountsData);
    }
  }

  ngOnInit(): void {
    this.displayedColumns = [
      'UtilityAccountNumber',
      'ZipCode',
      'City',
      'State',
      'StreetAddress1',
      'StreetAddress2',
      'Utility',
      'ISO',
      'Zone',
      ...(this.showRateCode ? ['RateCode'] : []),
      'NewContractStartDate',
      'Errors',
      'Action'
    ];

    if (!this.showStartDate) {
      this.displayedColumns = this.displayedColumns.filter((x) => x !== 'NewContractStartDate');
    }

    this.selectLoggedInUser$ = this.store.select(loggedInUserSelector).subscribe((user) => {
      if (user && !user.expired) {
        this.uhIdentity$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((uhUser) => {
          this.userIdentity = uhUser;
        });
      }
    });

    this.uhConfig$ = this.store.select(configurationSelector).subscribe((c) => {
      this.uhConfig = c;
    });

    this.nextButtonClicked$ = this.nextButtonClicked.subscribe(() => {
      this.validateAccounts();
    });

    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
    });

    this.supplierUtilityInformation$ = this.store.select(supplierUtilityInformationSelector).subscribe((supplierUtilityInformation) => {
      this.supplierUtilityInformation = supplierUtilityInformation;
    });
    this.init.emit(this);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.annualUsageRequired) {
      this.annualUsageRequired = changes?.annualUsageRequired?.currentValue;
    }
    if (changes?.mandatoryLoadProfile) {
      this.mandatoryLoadProfile = changes?.mandatoryLoadProfile?.currentValue;
      this.clearValidationMessages();
      if (this.utilityAccounts?.length > 0) {
        this.validateAccount(this.utilityAccounts[this.utilityAccounts?.length - 1])
      }
    }
    if (changes?.mandatoryStartDate) {
      this.mandatoryStartDate = changes?.mandatoryStartDate?.currentValue;
    }
  }

  ngOnDestroy(): void {
    this.selectLoggedInUser$?.unsubscribe();
    this.uhIdentity$?.unsubscribe();
    this.nextButtonClicked$.unsubscribe();
    this.uhConfig$?.unsubscribe();
    this.isReadOnly$?.unsubscribe();
    this.utilityInformation$?.unsubscribe();
    this.utilityRateCodes$?.unsubscribe();
    this.supplierUtilityInformation$?.unsubscribe();
  }

  public get showNewUtilityAccountInput(): boolean {
    return !this.isReadOnly && this.dataSource.data.length < 100;
  }

  private initNewAccountsData(): void {
    this.newAccountsData = {
      utilityAccountNumber: '',
      rateCode: '',
      utilityDetails: {
        iso: '',
        zone: '',
        utilityShort: '',
        bundledRate: '',
        weatherZone: '',
        commodity: this.quoteRequestState?.commodity,
        annualUsage: 0,
        uom: '',
        dgsShortName: '',
        powwrPricingName: '',
        supplierPricingName: '',
        risk360Name: '',
      },
      address: {
        zipCode: this.utilityAccounts?.length === 0 ? this.quoteRequestState?.zipCode : '',
        city: '',
        state: this.utilityAccounts?.length === 0 ? this.quoteRequestState?.state.toString() : '',
        country: '',
        addressLine1: '',
        addressLine2: '',
        quoteRequestId: this.quoteRequestId
      }
    }
  }

  public getFullValidationMessage(utilityAccount: UtilityAccount) {
    this.validateAccounts();
    return of(utilityAccount?.validationInfo?.getAllValidationMessages()?.join('\r\n'));
  }

  public clearValidationMessages() {
    this.utilityAccounts?.map(x => x.validationInfo = new ClassValidation());
    this.validateAccounts();
  }

  public propInvalid(utilityAccount: UtilityAccount, propName: string): boolean {
    return this.isReadOnly ? false : utilityAccount.validationInfo?.propValid(propName) === false;
  }

  populate(utilityAccounts: any[], utilityInformation: UtilityInformation, state?: QuoteRequestState, quoteConfiguration?: QuoteConfiguration) {
    this.utilityInformation = utilityInformation;
    this.quoteRequestState = state;
    this.utilityAccounts = utilityAccounts ?? [];
    this.quoteConfiguration = quoteConfiguration;
    this.isGas = this.quoteRequestState?.commodity === CommodityType.Gas;

    this.utilityAccounts.forEach((utilityAccount) => {
      if (!utilityAccount.rateCodeDetails)
        utilityAccount.rateCodeDetails = { "rateClass": utilityAccount.rateClass, "rateCode": utilityAccount.rateCode }
      if (utilityAccount?.address?.state && utilityAccount?.utilityDetails) {
        const utilityAccountNumber = utilityAccount?.utilityAccountNumber;
        this.madeRequestForUtilityInformation[utilityAccountNumber] = false;

        this.utilityInformation$ = this.utilityInformationService.getUtilityInformation(utilityAccount.address.state, this.quoteRequestState.quoteRequestType, this.quoteRequestState?.commodity, utilityAccount.address.zipCode).pipe(
          catchError((error) => {
            console.error('Error fetching utility information:', error);
            this.madeRequestForUtilityInformation[utilityAccountNumber] = true;
            this.utilityAccountService.validateUtilityInformation(utilityAccount);
            return of([]);
          })
        ).subscribe((utilityInformation) => {
          this.madeRequestForUtilityInformation[utilityAccountNumber] = true;

          if (utilityInformation.length > 0) {
            this.utilityAccountService.addUtilities(utilityAccount?.address?.zipCode, utilityInformation);
          }

          this.utilityAccountService.validateUtilityInformation(utilityAccount);
        });

        this.fetchAndAddUtilityRateCodes(utilityAccount);
      }
    });

    /*

    NOTE: This functionality is currently not active. In addition further analysis is required to understand its integration
    within the multiple-utilities framework. Following discussions with the product manager, we've
    commented out this code for potential future implementation.

    Code Summary:
    If the quote request type is 'Matrix' or 'Custom', the 'MeterNumber' column would be added to the displayed columns.
    Additionally:
      - If `useNameKey` is enabled in utility information, a 'NameKey' column would be added. Each utility account’s
        'nameKey' would be populated with either an existing value or a substring of the organisation name.
      - If `useServiceNumber` is enabled in utility information, a 'ServiceKey' column would be added, and the same
        population rule would apply.

    This functionality will remain in consideration for future releases as we assess its alignment with the multiple-utilities functionality.

    if (this.quoteRequestState?.quoteRequestType === 'Matrix' || this.quoteRequestState?.quoteRequestType === 'Custom') {
      this.displayedColumns.splice(1, 0, "MeterNumber");
      this.utilityAccounts.forEach((x) => (x.meterNumber = x.meterNumber ? x.meterNumber : null));

      if (this.utilityInformation.useNameKey === true) {
        this.displayedColumns.splice(this.displayedColumns.length - 2, 0, "NameKey");
        this.utilityAccounts.forEach((x) => (x.nameKey = x.nameKey ? x.nameKey : this.quoteRequestState?.organisationName.substring(0, utilityInformation.keyLength)));
      }
      if (this.utilityInformation.useServiceNumber === true) {
        this.displayedColumns.splice(this.displayedColumns.length - 2, 0, "ServiceKey");
        this.utilityAccounts.forEach((x) => (x.serviceKey = x.serviceKey ? x.serviceKey : this.quoteRequestState?.organisationName.substring(0, utilityInformation.keyLength)));
      }
      this.validateKeyAndMeterNumber = true;
    }
    */

    this.clearValidationMessages();
    this.validateAccounts();
    this.rebindTable();

    this.initNewAccountsData();

    if (this.newAccountsData?.address?.zipCode) {
      this.zipLookup(this.newAccountsData);
    }
  }

  public validateAccounts() {
    let salesType: string;
    this.salesType$.subscribe(x => { salesType = x; }).unsubscribe();
    if (salesType === SalesType.renewal) {
      return (this.anonymousObject);
    }
    else {
      return this.utilityAccountService.validateAll(
        this.utilityAccounts,
        this.supplierUtilityInformation,
        this.quoteRequestState,
        this.userIdentity.currentOrganisation,
        this.madeRequestForUtilityInformation,
        this.madeRequestForRatecodes,
        this.isReadOnly,
        this.showRateCode,
        this.showStartDate,
        this.mandatoryLoadProfile,
        this.mandatoryStartDate,
        this.validateKeyAndMeterNumber,
      );
    }
  }

  private validateAll(): { valid: boolean; entities: ClassValidation[] } {
    this.checkFooterValues();
    const isEmptyFooter = this.emptyFooterRow();
    return isEmptyFooter && this.validateAccounts();
  }

  checkFooterValues() {
    this.footerUanInvalid = !this.uanFooter?.nativeElement.value;
    this.footerRateCodeInvalid = !this.rateCodeFooter?.nativeElement.value;
    this.footerStartDateInvalid = !this.startDateFooter?.nativeElement.value;
    if (this.validateKeyAndMeterNumber === true) {
      if (this.utilityInformation.useNameKey === true) this.footerNameKeyInvalid = !this.nameKeyFooter?.nativeElement.value;
      if (this.utilityInformation.useServiceNumber === true) this.footerServiceKeyInvalid = !this.nameKeyFooter?.nativeElement.value;
      if (this.utilityInformation.meterNumberIsMandatory === true) this.footerMeterNumberInvalid = !this.meterNumberFooter?.nativeElement.value;
    }
    this.footerZipCodeInvalid = !this.uanFooter?.nativeElement.value;
    this.footerCityInvalid = !this.cityFooter?.nativeElement.value;
    this.footerStateInvalid = !this.stateFooter?.nativeElement.value;
    this.footerStreetAddress1Invalid = !this.streetAddress1Footer?.nativeElement.value;
    this.footerUtilityInvalid = !this.utilityFooter?.nativeElement.value;
    this.footerIsoInvalid = !this.isoFooter?.nativeElement.value;
    this.footerZoneInvalid = !this.zoneFooter?.nativeElement.value;
    this.footerWeatherZoneInvalid = this.utilityAccountService.isErcotIso(this.isoFooter?.nativeElement.value) ? !this.weatherZoneFooter?.nativeElement.value : false;
  }

  completedFooterRow() {
    if (this.validateKeyAndMeterNumber === true) {
      if (
        this.uanFooter?.nativeElement.value &&
        this.rateCodeFooter?.nativeElement.value &&
        (this.showStartDate ? this.startDateFooter?.nativeElement.value : true) &&
        (this.utilityInformation.useNameKey === true ? this.nameKeyFooter?.nativeElement.value : true) &&
        (this.utilityInformation.useServiceNumber === true ? this.serviceKeyFooter?.nativeElement.value : true) &&
        (this.utilityInformation.meterNumberIsMandatory === true ? this.meterNumberFooter?.nativeElement.value : true) &&
        this.zipCodeFooter?.nativeElement.value &&
        this.cityFooter?.nativeElement.value &&
        this.stateFooter?.nativeElement.value &&
        this.streetAddress1Footer?.nativeElement.value &&
        this.utilityFooter?.nativeElement.value &&
        this.isoFooter?.nativeElement.value &&
        this.zoneFooter?.nativeElement.value
          (this.utilityAccountService.isErcotIso(this.isoFooter?.nativeElement.value) ? this.weatherZoneFooter?.nativeElement.value : true)
      ) {
        return true;
      }
    } else {
      if (
        this.uanFooter?.nativeElement.value &&
        this.rateCodeFooter?.nativeElement.value &&
        (this.showStartDate ? this.startDateFooter?.nativeElement.value : true)
      ) {
        return true;
      }
    }
  }

  emptyFooterRow() {
    if (this.validateKeyAndMeterNumber === true) {
      if (!this.uanFooter?.nativeElement.value &&
        !this.rateCodeFooter?.nativeElement.value &&
        !this.startDateFooter?.nativeElement.value &&
        (this.utilityInformation.useNameKey === true ? !this.nameKeyFooter?.nativeElement.value : true) &&
        (this.utilityInformation.useServiceNumber === true ? !this.serviceKeyFooter?.nativeElement.value : true) &&
        (this.utilityInformation.meterNumberIsMandatory === true ? !this.meterNumberFooter?.nativeElement.value : true) &&
        !this.zipCodeFooter?.nativeElement.value &&
        !this.cityFooter?.nativeElement.value &&
        !this.stateFooter?.nativeElement.value &&
        !this.streetAddress1Footer?.nativeElement.value &&
        !this.utilityFooter?.nativeElement.value &&
        !this.isoFooter?.nativeElement.value &&
        !this.zoneFooter?.nativeElement.value &&
        (this.utilityAccountService.isErcotIso(this.isoFooter?.nativeElement.value) ? !this.weatherZoneFooter?.nativeElement.value : true)) {
        return true;
      }
    } else {
      if (!this.uanFooter?.nativeElement.value &&
        !this.rateCodeFooter?.nativeElement.value &&
        !this.startDateFooter?.nativeElement.value &&
        !this.zipCodeFooter?.nativeElement.value &&
        !this.cityFooter?.nativeElement.value &&
        !this.stateFooter?.nativeElement.value &&
        !this.streetAddress1Footer?.nativeElement.value &&
        !this.utilityFooter?.nativeElement.value &&
        !this.isoFooter?.nativeElement.value &&
        !this.zoneFooter?.nativeElement.value &&
        (this.utilityAccountService.isErcotIso(this.isoFooter?.nativeElement.value) ? !this.weatherZoneFooter?.nativeElement.value : true)) {
        return true;
      }
    }
  }

  validateFooterRow() {
    this.checkFooterValues();

    if (!this.footerUanInvalid &&
      !this.footerRateCodeInvalid &&
      !this.footerStartDateInvalid &&
      (this.validateKeyAndMeterNumber === true &&
        (this.utilityInformation.useNameKey === true ? !this.footerNameKeyInvalid : true) &&
        (this.utilityInformation.useServiceNumber === true ? !this.footerServiceKeyInvalid : true) &&
        (this.utilityInformation.meterNumberIsMandatory === true ? !this.footerMeterNumberInvalid : true))) {
      return true;
    }

    return false;
  }

  private rebindTable() {
    this.newAccountsData = deepClone(this.newAccountsData);

    this.newAccountsData.utilityAccountNumber = null;
    this.newAccountsData.newContractStartDate = null;
    this.newAccountsData.rateCodeDetails = null;
    this.newAccountsData.validationInfo = null;
    this.newAccountsData.address.zipCode = null;
    this.newAccountsData.address.city = null;
    this.newAccountsData.address.state = null;
    this.newAccountsData.address.addressLine1 = null;
    this.newAccountsData.address.addressLine2 = null;
    this.newAccountsData.utilityDetails.utilityShort = null;
    this.newAccountsData.utilityDetails.zone = null;
    this.newAccountsData.utilityDetails.iso = null;
    this.newAccountsData.utilityDetails.bundledRate = null;
    this.newAccountsData.utilityDetails.commodity = null;
    this.newAccountsData.utilityDetails.annualUsage = null;
    this.newAccountsData.utilityDetails.uom = null;
    this.newAccountsData.utilityDetails.dgsShortName = null;
    this.newAccountsData.utilityDetails.powwrPricingName = null;
    this.newAccountsData.utilityDetails.supplierPricingName = null;
    this.newAccountsData.utilityDetails.risk360Name = null;

    if (this.newAccountsData.utilityDetails.weatherZone) {
      this.newAccountsData.utilityDetails.weatherZone = null;
    }

    if (this.validateKeyAndMeterNumber === true) {
      this.newAccountsData.nameKey = null;
      this.newAccountsData.serviceKey = null;
      this.newAccountsData.meterNumber = null;
    }

    this.resetFooterValid();
    this.dataSource = new MatTableDataSource(this.utilityAccounts);
  }

  private resetFooterValid() {
    this.footerUanInvalid = false;
    this.footerRateCodeInvalid = false;
    this.footerStartDateInvalid = false;
    if (this.validateKeyAndMeterNumber === true) {
      this.footerNameKeyInvalid = false;
      this.footerServiceKeyInvalid = false;
      this.footerMeterNumberInvalid = false;
    }
  }

  public rowChanged(utilityAccount: UtilityAccount) {
    if (!this.uhConfig.configuration.allowDifferentContractStartDates) {
      this.utilityAccounts.forEach((ua) => (ua.newContractStartDate = utilityAccount.newContractStartDate));
    }
    return this.validateAccount(utilityAccount);
  }

  public rateCodeChanged(utilityAccount: UtilityAccount) {
  if (!utilityAccount) { return; }

  const selectedRateCode = (utilityAccount.rateCode ?? '').trim();

  if (!selectedRateCode) {
    utilityAccount.rateCodeDetails = null as any;
    utilityAccount.rateClass = '';
    this.applyToAll.generateApplyToAllPrompt('Load Profile', '', 'rateCode', 'rateCode');
    return this.validateAccount(utilityAccount);
  }

  const selectedRateCodeDetails = (this.findRateCodes(utilityAccount) || []).find(r => r.rateCode === selectedRateCode) ?? null;

  utilityAccount.rateCodeDetails = selectedRateCodeDetails as any;
  utilityAccount.rateClass       = selectedRateCodeDetails?.rateClass ?? '';

  this.applyToAll.generateApplyToAllPrompt('Load Profile', selectedRateCode, 'rateCode', 'rateCode');
  return this.validateAccount(utilityAccount);
}


  validateProfileChanged($event) {
    const currentLoadProfile = this.quickQuoteUtilityService.getLoadProfile();
    this.isValidProfile = currentLoadProfile === this.newAccountsData?.rateCodeDetails.rateCode;
  }

  public contractStartDateChanged(utilityAccount: UtilityAccount) {
    if (this.utilityAccounts.filter((x) => x.newContractStartDate != utilityAccount.newContractStartDate)?.length > 0) {
      this.applyToAll.generateApplyToAllPrompt('New Contract Start Month', utilityAccount.newContractStartDate, 'newContractStartDate');

      return this.validateAccount(utilityAccount);
    }
  }

  private validateAccount(utilityAccount: UtilityAccount) {
    return this.utilityAccountService.validate(
      utilityAccount,
      this.supplierUtilityInformation,
      this.quoteRequestState,
      this.madeRequestForUtilityInformation[utilityAccount?.utilityAccountNumber],
      this.madeRequestForRatecodes[utilityAccount?.utilityDetails?.utilityShort],
      this.isReadOnly,
      this.utilityAccounts,
      this.userIdentity.currentOrganisation,
      this.showRateCode,
      this.showStartDate,
      this.mandatoryLoadProfile,
      this.mandatoryStartDate,
      this.validateKeyAndMeterNumber,
    );
  }

  startDateValid(ua: UtilityAccount) {
    return ua.newContractStartDate;
  }

  public storeRow(ua?: UtilityAccount) {
    if (this.completedFooterRow() || (this.showStartDate && ua?.newContractStartDate)) {
      this.newAccountsData = this.utilityAccountService.validateUniqueness(ua ?? this.newAccountsData, this.utilityAccounts, true, this.validateKeyAndMeterNumber);

      if (!this.showStartDate) {
        this.newAccountsData = { ...this.newAccountsData, newContractStartDate: new Date().toDateString() };
      }

      const accountString = this.validateKeyAndMeterNumber && this.newAccountsData.meterNumber
        ? `${this.newAccountsData?.utilityAccountNumber ?? ''}\t${this.newAccountsData.meterNumber}`
        : this.newAccountsData?.utilityAccountNumber ?? '';

      const accountList = this.utilityAccounts.map((x) => {
        return this.validateKeyAndMeterNumber && x.meterNumber
          ? `${x.utilityAccountNumber}${x.meterNumber}`
          : x.utilityAccountNumber;
      });

      const results = this.utilityAccountService.parsePastedData(
        accountString,
        accountList,
        this.utilityInformation,
        this.validateKeyAndMeterNumber
      );

      if (results.duplicates?.length && ua.utilityAccountNumber) {
        this.messaging.alert(
          `The following ${getAccountName(this.utilityInformation)}s ${this.validateKeyAndMeterNumber === true ? '/Meter Number combinations' : ''} already appear in your quote. Any duplicates have been removed: ${results.duplicates.join(',')}`
        );
        this.rebindTable();
        return;
      }
      this.utilityAccounts.push({ ...this.newAccountsData });
      const valInfo = this.validateAccount(this.utilityAccounts[this.utilityAccounts.length - 1]);
      this.rebindTable();
    }
  }

  getErrors() {
    return of(this.utilityAccounts?.map(x => x?.validationInfo?.fields?.rateCode?.errors)[0]);
  }

  public removeUtilityAccount(utilityAccount: UtilityAccount) {
    const index = this.utilityAccounts.indexOf(utilityAccount);
    if (index >= 0) {
      this.utilityAccounts.splice(index, 1);
    }
    this.rebindTable();
  }

  public removeInvalidUAs() {
    this.validateAccounts();
    this.utilityAccounts = this.utilityAccounts.filter((f) => f.validationInfo.valid);
    this.rebindTable();
  }

  public removeAllUAs() {
    this.messaging
      .confirm(`Are you sure you want to remove all ${getAccountName(this.utilityInformation)}s from this page?`, `Remove All ${getAccountName(this.utilityInformation)}s`, {
        primaryButtonText: `Remove All ${getAccountName(this.utilityInformation)}s`,
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.utilityAccounts = [];
          this.rebindTable();
        }
      });
  }

  public chosenMonthHandler(
    normalizedMonth: Moment,
    datepicker: MatDatepicker<Moment>,
    utilityAccount: UtilityAccount,
    addNewRow: boolean
  ) {
    utilityAccount.newContractStartDate = normalizedMonth.toISOString();
    datepicker.close();

    // So we can kick off the new row binding, but copy utilityAccount object as it'll get nulled in storeRow.
    const uaCopy = { ...utilityAccount };

    if (addNewRow) {
      this.storeRow(utilityAccount);
    }

    if (this.uhConfig?.configuration?.allowDifferentContractStartDates) {
      this.contractStartDateChanged(uaCopy);
    } else {
      this.utilityAccounts.forEach((ua) => (ua.newContractStartDate = uaCopy.newContractStartDate));
    }
  }

  public get isValid(): boolean {
    this.validityChecked = true;
    return this.validateAll()?.valid && this.utilityAccounts?.length > 0;
  }

  public getData() {
    // TODO: Maybe change service to return this structure
    if (!this.utilityAccounts) {
      return [];
    }

    this.utilityAccounts.forEach((ua) => {
      ua.rateClass = ua.rateCodeDetails?.rateClass ?? ua.rateClass;
      ua.rateCode = ua.rateCodeDetails?.rateCode ?? ua.rateCode;
    });

    return this.utilityAccounts;
  }

  public getDataSnapshot(): UtilityAccount[] {
    const snapshot = [];
    this.dataSource.data.forEach((ua) => snapshot.push(Object.assign({}, ua)));
    return snapshot;
  }

  public onPaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedText = clipboardData.getData('text');

    const results = this.utilityAccountService.parsePastedData(
      pastedText,
      this.utilityAccounts.map((x) => this.validateKeyAndMeterNumber === true ? x.utilityAccountNumber + x.meterNumber : x.utilityAccountNumber),
      this.utilityInformation, this.validateKeyAndMeterNumber);

    if (results.duplicates?.length) {
      this.messaging.alert(
        `The following ${getAccountName(this.utilityInformation)}s ${this.validateKeyAndMeterNumber === true ? '/Meter Number combinations' : ''} already appear in your quote. Any duplicates have been removed: ${results.duplicates.join(',')}`
      );
    }
    if (results.rowLimitExceeded) {
      this.messaging.alert(`Please enter up to 100 ${getAccountName(this.utilityInformation)}s`, `Maximum ${getAccountName(this.utilityInformation)}s Exceeded`);
    }

    // This is where the parsed results just get added to the current data set.
    const defaultUtilityDetails = {
      utilityShort: '',
      iso: '',
      zone: '',
      weatherZone: '',
      bundledRate: '',
      commodity: this.quoteRequestState.commodity,
      annualUsage: 0,
      uom: '',
      dgsShortName: '',
      powwrPricingName: '',
      risk360Name: '',
      supplierPricingName: ''
    };

    const defaultAddress = {
      zipCode: '',
      city: '',
      state: '',
      country: '',
      addressLine1: ''
    };

    results.newRows.forEach((row) => {
      row.utilityDetails = {
        ...defaultUtilityDetails,
        ...row.utilityDetails
      };

      row.address = {
        ...defaultAddress,
        ...row.address
      };
    });

    results.newRows.map((newRow, index) => {
      if (index === 0) {
        newRow.address = {
          ...this.newAccountsData?.address,
          ...(newRow.address?.zipCode ? newRow.address : {})
        };
      }

      if (newRow.address?.zipCode) {
        this.zipLookup(newRow);
      }

      newRow.rateCode = '';
      this.utilityAccounts.push(newRow);
    });

    this.validateAccounts();
    this.rebindTable();

    event.preventDefault();
  }

  validate(control: AbstractControl): ValidationErrors {
    return this.frmUtilityAccountsGrid?.valid ? null : { invalidForm: { valid: false, message: 'Supply Details Invalid' } };
  }

  registerOnValidatorChange?(fn: () => void): void { }

  writeValue(val: any): void {
    if (val) {
      this.frmUtilityAccountsGrid?.patchValue(val, { emitEvent: false });
    }
    this.updated.next('form');
  }

  registerOnChange(fn: any): void {
    this.register$ = this.frmUtilityAccountsGrid?.valueChanges.subscribe((x) => fn(this.frmUtilityAccountsGrid.getRawValue()));
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private resetRequestFlags(utilityAccount: UtilityAccount): void {
    const utilityAccountNumber = utilityAccount?.utilityAccountNumber;

    this.madeRequestForUtilityInformation[utilityAccountNumber] = false;
    this.madeRequestForRatecodes[utilityAccountNumber] = false;
  }

  public zipLookup(utilityAccount: UtilityAccount, isBlurEvent = false): void {
    const VALID_ZIP_LENGTH = 5;
    const zipCode = utilityAccount?.address?.zipCode;
    const utilityAccountNumber = utilityAccount?.utilityAccountNumber;

    if (!zipCode) {
      this.clearUtilityAddress(utilityAccount);
      this.clearUtilityDetailsValues(utilityAccount);
      utilityAccount.rateCode = '';
      utilityAccount.rateCodeDetails = null;
      return;
    }

    if (isBlurEvent) {
      this.utilityAccountService.validateZipCode(utilityAccount);
      return;
    }

    if (zipCode.length !== VALID_ZIP_LENGTH) {
      return;
    }

    this.zipCodeLoadingMap[utilityAccountNumber] = true;

    this.usLookup.lookupStateFromZipCode(zipCode)
      .pipe(
        take(1),
        finalize(() => {
          this.zipCodeLoadingMap[utilityAccountNumber] = false;
          this.utilityAccountService.validateZipCode(utilityAccount);
        })
      )
      .subscribe({
        next: (results) => {
          if (results?.stateCode && results.city) {
            this.updateUtilityAddress(utilityAccount, results.stateCode, results.city, zipCode, utilityAccountNumber);
          } else {
            console.warn('Incomplete results returned for ZIP code lookup', results);
          }
        },
        error: (error) => {
          console.error(`ZIP code lookup failed for zipCode: ${zipCode} and account: ${utilityAccountNumber}`, error);
        }
      });
  }

  private clearUtilityAddress(utilityAccount: UtilityAccount): void {
    utilityAccount.address.city = '';
    utilityAccount.address.state = '';
  }

  private updateUtilityAddress(utilityAccount: UtilityAccount, stateCode: string, city: string, zipCode: string, utilityAccountNumber: string): void {
    utilityAccount.address.state = stateCode;
    utilityAccount.address.city = city;
    this.utilityAccountNumber = utilityAccountNumber;
    this.selectUtilityInformation(utilityAccount);
  }

  public findUtilities(zipCode: string): UtilityInformation[] {
    return this.utilityAccountService.findUtilities(zipCode);
  }

  public findZoneInformation(zipCode: string, utilityShort: string): UtilityZoneInformation[] {
    const utilityInformation = this.findUtilities(zipCode);
    const utility = utilityInformation?.find((utility) => utility.utilityShort === utilityShort);
    if (this.userIdentity.currentOrganisation.externalId.toLowerCase() === 'cleansky-001-s')
    {
      return utility?.utilityZoneInformation.filter(z => !z.loadZone.toLowerCase().includes("_hub")) ?? [];
    }
    else
    {
      return utility?.utilityZoneInformation ?? [];
    }
  }

  public findRateCodes(utilityAccount: UtilityAccount): UtilityRateCode[] {
    return this.utilityAccountService.findRateCodes(utilityAccount);
  }

  public selectUtilityInformation(utilityAccount: UtilityAccount): void {
    const utilityAccountNumber = utilityAccount?.utilityAccountNumber;
    this.madeRequestForUtilityInformation[utilityAccountNumber] = false;

    this.utilityInformation$ = this.utilityInformationService.getUtilityInformation(utilityAccount.address.state, this.quoteRequestState.quoteRequestType, this.quoteRequestState.commodity, utilityAccount.address.zipCode).pipe(
      catchError((error) => {
        console.error('Error fetching utility information:', error);
        this.madeRequestForUtilityInformation[utilityAccountNumber] = true;
        this.utilityAccountService.validateUtilityInformation(utilityAccount);
        return of([]);
      })
    ).subscribe((utilityInformation) => {
      this.madeRequestForUtilityInformation[utilityAccountNumber] = true;
      this.utilityAccountService.addUtilities(utilityAccount?.address?.zipCode, utilityInformation);

      if (utilityInformation.length === 1) {
        const clonedUtilityAccount = deepClone(utilityAccount);

        utilityAccount.utilityDetails = {
          ...clonedUtilityAccount.utilityDetails,
          utilityShort: utilityInformation[0].utilityShort
        };

        if (utilityAccount?.utilityDetails?.utilityShort) {
          this.onUtilitySelected(utilityAccount);
        }
      } else {
        this.clearUtilityDetailsValues(utilityAccount);
      }

      this.utilityAccountService.validateUtilityInformation(utilityAccount);
    });

    this.fetchAndAddUtilityRateCodes(utilityAccount);
  }

  public isUtilityDetailFilled(utilityDetails: UtilityDetails): boolean {
    const utilityShortFilled = utilityDetails?.utilityShort != null && utilityDetails.utilityShort !== '';

    if (!this.isGas) {
      const isoFilled = utilityDetails?.iso != null && utilityDetails.iso !== '';
      return utilityShortFilled && isoFilled;
    }

    return utilityShortFilled;
  }

  public onUtilitySelected(utilityAccount: UtilityAccount): void {
    const utilityInformation = this.utilityAccountService.listOfUtilitiesByZipCode.find(utilities => utilities.zipCode === utilityAccount?.address?.zipCode);
    const selectedUtility = utilityInformation?.utilityInformation?.find(utility => utility.utilityShort === utilityAccount?.utilityDetails?.utilityShort);

    if (selectedUtility) {
      utilityAccount.utilityDetails = {
        ...utilityAccount.utilityDetails,
        iso: selectedUtility.iso,
        bundledRate: this.quoteConfiguration?.utilityDetails?.bundledRate,
        annualUsage: this.quoteConfiguration?.annualUsage,
        commodity: this.quoteRequestState.commodity,
        uom: selectedUtility.uom,
        dgsShortName: selectedUtility.dgsShortName,
        powwrPricingName: selectedUtility.powwrPricingName,
        supplierPricingName: selectedUtility.supplierPricingName,
        risk360Name: selectedUtility.risk360Name,
      };

      if (this.userIdentity.currentOrganisation.externalId.toLowerCase() === 'cleansky-001-s')
      {
        if (selectedUtility.utilityZoneInformation.filter(z => !z.loadZone.toLowerCase().includes("_hub")).length === 1) {
          utilityAccount.utilityDetails = {
            ...utilityAccount.utilityDetails,
            zone: selectedUtility.utilityZoneInformation[0].loadZone,
            weatherZone: selectedUtility.utilityZoneInformation[0].weatherZone,
          }
        }
      }
      else
      {
        if (selectedUtility.utilityZoneInformation.length === 1) {
          utilityAccount.utilityDetails = {
            ...utilityAccount.utilityDetails,
            zone: selectedUtility.utilityZoneInformation[0].loadZone,
            weatherZone: selectedUtility.utilityZoneInformation[0].weatherZone,
          }
        }
      }

      this.fetchAndAddUtilityRateCodes(utilityAccount);

      if (this.utilityAccountService.isErcotIso(selectedUtility.iso)) {
        this.addWeatherZone();
      }
    } else {
      this.clearUtilityDetailsValues(utilityAccount);
      utilityAccount.rateCode = '';
      utilityAccount.rateCodeDetails = null;
      this.utilityAccountService.validateUtilityAndRateCodes(utilityAccount, this.supplierUtilityInformation, this.userIdentity.currentOrganisation, this.showRateCode);
    }
  }

  private addWeatherZone() {
    const zoneIndex = this.displayedColumns.indexOf('Zone');

    if (zoneIndex !== -1 && !this.displayedColumns.includes('WeatherZone')) {
      this.displayedColumns.splice(zoneIndex + 1, 0, 'WeatherZone');
    }
  }

  private clearUtilityDetailsValues(utilityAccount: UtilityAccount) {
    this.resetRequestFlags(utilityAccount);

    utilityAccount.utilityDetails = {
      ...utilityAccount.utilityDetails,
      iso: '',
      zone: '',
      weatherZone: '',
      utilityShort: '',
      dgsShortName: '',
      powwrPricingName: '',
      supplierPricingName: '',
      risk360Name: '',
      uom: ''
    }
  }

  private addToRequestedRateCodeStates(utilityAccount: UtilityAccount): void {
    const requestedRateCodeUtilities = new Set(this.requestedRateCodeUtilities);
    const utilityShort = utilityAccount?.utilityDetails?.utilityShort;

    if (utilityShort) {
      requestedRateCodeUtilities.add(utilityShort);
    }

    this.requestedRateCodeUtilities = Array.from(requestedRateCodeUtilities);
  }

  private fetchAndAddUtilityRateCodes(utilityAccount: UtilityAccount): void {
    const { commodity } = this.quoteRequestState || {};
    const { iso, utilityShort } = utilityAccount?.utilityDetails || {};

    const isElectricity = commodity === CommodityType.Electricity;
    const electrictyCriteria = isElectricity && iso && utilityShort;
    const gasCriteria = this.isGas && utilityShort;
    const isRateCodeFetched = this.requestedRateCodeUtilities.includes(utilityAccount?.utilityDetails?.utilityShort);
    this.madeRequestForRatecodes[utilityShort] = false;

    if ((electrictyCriteria || gasCriteria) && !isRateCodeFetched) {
      this.newAccountsData.rateCode = '';
      this.loadingRateCodes[utilityShort] = true;

      this.utilityRateCodes$ = this.utilityInformationService.getUtilityRateCodesByCriteria(
        utilityAccount?.address?.state,
        commodity,
        utilityAccount?.utilityDetails
      )
      .pipe(
        catchError((error) => {
          this.loadingRateCodes[utilityShort] = false;
          this.madeRequestForRatecodes[utilityShort] = true;
          console.error('Error fetching utility rate codes:', error);
          this.utilityAccountService.validateUtilityAndRateCodes(utilityAccount, this.supplierUtilityInformation, this.userIdentity.currentOrganisation, this.showRateCode);
          return of([]);
        })
      )
        .subscribe((rateCodes) => {
        this.loadingRateCodes[utilityShort] = false;
        this.madeRequestForRatecodes[utilityShort] = true;
        this.addToRequestedRateCodeStates(utilityAccount);
        this.utilityAccountService.addRateCodes(utilityAccount?.utilityDetails?.utilityShort, rateCodes);
          this.utilityAccountService.validateUtilityAndRateCodes(utilityAccount, this.supplierUtilityInformation, this.userIdentity.currentOrganisation, this.showRateCode, rateCodes);
      });
    } else if (isRateCodeFetched) {
      this.loadingRateCodes[utilityShort] = false;
      this.madeRequestForRatecodes[utilityShort] = true;
      const rateCodes = this.utilityAccountService.findRateCodes(utilityAccount);
      this.utilityAccountService.validateUtilityAndRateCodes(utilityAccount, this.supplierUtilityInformation, this.userIdentity.currentOrganisation, this.showRateCode, rateCodes);
    }
  }

  public hasMoreThanOneUtility(zipCode: string): boolean {
    const utilities = this.utilityAccountService.findUtilities(zipCode) ?? [];
    const hasMoreThanOneUtility = utilities.length > 1;

    return hasMoreThanOneUtility;
  }

  public hasMoreThanOneUtilityZone(zipCode: string, utilityShort: string): boolean {
    const utilityZoneInformation = this.findZoneInformation(zipCode, utilityShort) ?? [];
    const hasMoreThanOneUtilityZone = utilityZoneInformation.length > 1;

    return hasMoreThanOneUtilityZone;
  }

  public hasRateCodes(utilityAccount: UtilityAccount): boolean {
    const rateCodes = this.utilityAccountService.findRateCodes(utilityAccount) ?? [];
    const hasRateCodes = rateCodes.length > 0;

    return hasRateCodes;
  }

  public disableIsoField(utilityAccount: UtilityAccount): boolean {
    const isElectric = this.quoteRequestState?.commodity === CommodityType.Electricity;
    const utilityShortSelected = utilityAccount?.utilityDetails?.utilityShort;
    const disableIsoField = isElectric && !!utilityShortSelected;

    return disableIsoField;
  }
}

function deepClone(obj: any) {
  return JSON.parse(JSON.stringify(obj));
}
